import React, { useEffect, useState } from 'react';
import { T } from '@transifex/react';
import { Check, Mail01, ShoppingBag03 } from '@untitledui-pro/icons/duotone';

import { PageHeading } from 'src/components/layout/PageHeading';

const PRICE_DKK = 45;

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i += 1) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `WH-${code}`;
};

const MobilePayButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#5A78FF] px-6 text-base font-semibold text-white transition-colors hover:bg-[#4A68EF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5A78FF]"
  >
    <T _str="Pay with" />
    <span className="font-bold tracking-tight">MobilePay</span>
  </button>
);

const PurchaseCard = ({ onPay }: { onPay: () => void }) => (
  <div className="flex flex-col gap-8 rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-8">
    <div className="flex items-start gap-4">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[var(--bg-active)]">
        <ShoppingBag03
          className="size-6 text-[var(--icon-active)]"
          aria-hidden
        />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold leading-tight tracking-tight text-[var(--text-primary)]">
          <T _str="Buy extra bag" />
        </h2>
        <p className="text-sm leading-normal text-[var(--text-secondary)]">
          <T _str="One-time purchase of an additional waste bag." />
        </p>
      </div>
    </div>

    <div className="flex items-baseline gap-2 border-t border-[var(--border-default)] pt-6">
      <span className="text-3xl font-semibold tracking-tight text-[var(--text-primary)]">
        {PRICE_DKK}
      </span>
      <span className="text-base font-medium text-[var(--text-secondary)]">
        DKK
      </span>
    </div>

    <MobilePayButton onClick={onPay} />
  </div>
);

type EmailStatus = 'idle' | 'sending' | 'sent';

const SuccessCard = ({
  code,
  onBuyAnother,
}: {
  code: string;
  onBuyAnother: () => void;
}) => {
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');

  useEffect(() => {
    if (emailStatus !== 'sending') return undefined;
    const timer = window.setTimeout(() => setEmailStatus('sent'), 900);
    return () => window.clearTimeout(timer);
  }, [emailStatus]);

  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-8 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-[var(--status-success)]/10">
        <Check className="size-8 text-[var(--status-success)]" aria-hidden />
      </div>
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold leading-tight tracking-tight text-[var(--text-primary)]">
          <T _str="Payment successful" />
        </h2>
        <p className="text-sm leading-normal text-[var(--text-secondary)]">
          <T _str="Show this code to the collection crew when handing over the extra bag." />
        </p>
      </div>
      <div className="flex w-full flex-col gap-2 rounded-xl border border-dashed border-[var(--border-strong)] bg-[var(--bg-default)] p-6">
        <span className="text-xs font-medium uppercase tracking-[0.05em] text-[var(--text-secondary)]">
          <T _str="Your code" />
        </span>
        <span className="font-mono text-2xl font-semibold tracking-[0.2em] text-[var(--text-primary)]">
          {code}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setEmailStatus('sending')}
        disabled={emailStatus !== 'idle'}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[var(--border-default)] bg-[var(--bg-default)] px-6 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-hover)] disabled:cursor-default disabled:opacity-100"
      >
        {emailStatus === 'sent' ? (
          <>
            <Check
              className="size-4 text-[var(--status-success)]"
              aria-hidden
            />
            <T _str="Sent to your email" />
          </>
        ) : emailStatus === 'sending' ? (
          <>
            <span
              aria-hidden
              className="size-4 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-[var(--text-primary)]"
            />
            <T _str="Sending…" />
          </>
        ) : (
          <>
            <Mail01 className="size-4 text-[var(--icon-default)]" aria-hidden />
            <T _str="Send to email" />
          </>
        )}
      </button>
      <button
        type="button"
        onClick={onBuyAnother}
        className="text-sm font-medium text-[var(--text-link)] hover:underline"
      >
        <T _str="Buy another bag" />
      </button>
    </div>
  );
};

const MobilePayOverlay = () => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[#0B1530]/95 backdrop-blur-sm">
    <div
      aria-hidden
      className="size-12 animate-spin rounded-full border-4 border-white/15 border-t-[#75BDEA]"
    />
    <p className="text-base font-medium text-white">
      <T _str="Opening MobilePay…" />
    </p>
  </div>
);

type Status = 'idle' | 'loading' | 'paid';

export default function Products() {
  const [status, setStatus] = useState<Status>('idle');
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'loading') return undefined;
    const timer = window.setTimeout(() => {
      setCode(generateCode());
      setStatus('paid');
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [status]);

  const handlePay = () => setStatus('loading');
  const handleReset = () => {
    setCode(null);
    setStatus('idle');
  };

  return (
    <div className="wh-ui flex h-full min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[480px] flex-col gap-8 px-6 py-8">
        <PageHeading
          title={<T _str="Products" />}
          subtitle={<T _str="Order add-on services for your property." />}
        />
        {status === 'paid' && code ? (
          <SuccessCard code={code} onBuyAnother={handleReset} />
        ) : (
          <PurchaseCard onPay={handlePay} />
        )}
      </div>
      {status === 'loading' && <MobilePayOverlay />}
    </div>
  );
}
